import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { automunityAPI } from '../lib/api';
import toast from 'react-hot-toast';
import './Requests.css';

interface Request {
  request_id: number;
  automation_id: number;
  automation_name?: string;
  type: string;
  priority: string;
  status: string;
  title: string;
  description?: string;
  submitted_by?: number;
  submitted_by_name?: string;
  assigned_to?: number;
  assigned_to_name?: string;
  kanban_column?: string;
  kanban_order: number;
  deadline?: string;
  created_at: string;
  updated_at?: string;
  time_entry_count?: number;
  comment_count?: number;
}

interface User {
  user_id: number;
  full_name: string;
  is_developer?: number;
}

interface StatusConfig {
  request_status_id: number;
  name: string;
  label: string;
  color: string;
  sort_order: number;
}

const FALLBACK_STATUSES: StatusConfig[] = [
  { request_status_id: 1, name: 'proposed', label: 'Proposed', color: '#6c757d', sort_order: 1 },
  { request_status_id: 3, name: 'waiting_on_requestor', label: 'Waiting on Requestor', color: '#ffc107', sort_order: 2 },
  { request_status_id: 4, name: 'in_rotation', label: 'In Rotation', color: '#fd7e14', sort_order: 3 },
  { request_status_id: 5, name: 'waiting_on_information', label: 'Waiting on Information', color: '#e83e8c', sort_order: 4 },
  { request_status_id: 6, name: 'backlogged', label: 'Backlogged', color: '#6f42c1', sort_order: 5 },
  { request_status_id: 7, name: 'in_progress', label: 'In Progress', color: '#007bff', sort_order: 6 },
  { request_status_id: 8, name: 'completed', label: 'Completed', color: '#28a745', sort_order: 7 },
  { request_status_id: 9, name: 'completed_in_review', label: 'Complete - In Review', color: '#20c997', sort_order: 8 },
  { request_status_id: 10, name: 'pending_deployment', label: 'Pending Deployment', color: '#17a2b8', sort_order: 9 },
  { request_status_id: 11, name: 'deployed_in_review', label: 'Deployed - In Review', color: '#6610f2', sort_order: 10 },
  { request_status_id: 12, name: 'cancelled', label: 'Cancelled', color: '#dc3545', sort_order: 11 },
  { request_status_id: 13, name: 'closed_in_production', label: 'Closed - In Production', color: '#155724', sort_order: 12 },
  { request_status_id: 14, name: 'closed_work_completed', label: 'Closed - Work Completed', color: '#1b4332', sort_order: 13 },
  { request_status_id: 15, name: 'closed_legacy', label: 'Closed - Legacy', color: '#8a6d3b', sort_order: 15 },
];

const PRIORITY_COLORS: Record<string, string> = {
  critical: '#dc3545',
  high: '#fd7e14',
  medium: '#ffc107',
  low: '#6c757d',
};

const TYPE_ICONS: Record<string, string> = {
  feature: '✨',
  bug: '🐛',
  improvement: '🔧',
  question: '❓',
  administrative: '📋',
};

const Requests: React.FC = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<Request[]>([]);
  const [closedRequests, setClosedRequests] = useState<Request[]>([]);
  const [closedLoaded, setClosedLoaded] = useState(false);
  const [closedLoading, setClosedLoading] = useState(false);
  const [users, setUsers] = useState<User[]>([]);
  const [automations, setAutomations] = useState<Array<{automation_id: number; name: string}>>([]);
  const [statuses, setStatuses] = useState<StatusConfig[]>(FALLBACK_STATUSES);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'table' | 'kanban'>('table');
  const [activeTab, setActiveTab] = useState<string>('all');
  const [filterAssignee, setFilterAssignee] = useState<string>('mine');
  const [draggedRequest, setDraggedRequest] = useState<Request | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [selectedRequests, setSelectedRequests] = useState<Set<number>>(new Set());
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [isDeveloper, setIsDeveloper] = useState(false);
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({ key: 'created_at', direction: 'desc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [quickNoteRequestId, setQuickNoteRequestId] = useState<number | null>(null);
  const [quickNoteTitle, setQuickNoteTitle] = useState('');
  const [quickNoteText, setQuickNoteText] = useState('');
  const [quickNoteHours, setQuickNoteHours] = useState('');

  const fetchRequests = useCallback(async () => {
    try {
      setLoading(true);
      const response = await automunityAPI.getAllRequests();
      const result = await response.body.json() as any;
      
      let data: Request[] = [];
      if (Array.isArray(result)) {
        data = result;
      } else if (Array.isArray(result?.records)) {
        data = result.records;
      } else if (typeof result?.records === 'string') {
        try { data = JSON.parse(result.records); } catch { data = []; }
      }
      
      setRequests(Array.isArray(data) ? data.filter((r: any) => r && r.request_id) : []);
    } catch (err) {
      console.error('Error fetching requests:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Closed/resolved requests are fetched on demand (not in the default payload)
  // so the everyday view stays fast while history remains fully browsable.
  const fetchClosedRequests = useCallback(async (force = false) => {
    if (closedLoaded && !force) return;
    try {
      setClosedLoading(true);
      const response = await automunityAPI.getAllRequests({ includeClosed: true });
      const result = await response.body.json() as any;
      let data: Request[] = [];
      if (Array.isArray(result)) {
        data = result;
      } else if (Array.isArray(result?.records)) {
        data = result.records;
      } else if (typeof result?.records === 'string') {
        try { data = JSON.parse(result.records); } catch { data = []; }
      }
      setClosedRequests(Array.isArray(data) ? data.filter((r: any) => r && r.request_id) : []);
      setClosedLoaded(true);
    } catch (err) {
      console.error('Error fetching closed requests:', err);
    } finally {
      setClosedLoading(false);
    }
  }, [closedLoaded]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await automunityAPI.getUsers();
      const result = await response.body.json() as any;
      let data: User[] = [];
      if (Array.isArray(result)) data = result;
      else if (Array.isArray(result?.records)) data = result.records;
      else if (typeof result?.records === 'string') data = JSON.parse(result.records);
      setUsers(data.filter((u: any) => u && u.user_id && u.is_active));
    } catch (err) {
      console.error('Error fetching users:', err);
    }
  }, []);

  const fetchStatuses = useCallback(async () => {
    try {
      const response = await automunityAPI.getRequestStatuses();
      const result = await response.body.json() as any;
      let data: StatusConfig[] = [];
      if (Array.isArray(result)) data = result;
      else if (Array.isArray(result?.records)) data = result.records;
      else if (typeof result?.records === 'string') {
        try { data = JSON.parse(result.records); } catch { data = []; }
      }
      if (data.length > 0) {
        // Remove proposed_in_review — it's been merged into 'proposed'
        const filtered = data.filter(s => s.name !== 'proposed_in_review');
        setStatuses(filtered.sort((a, b) => a.sort_order - b.sort_order));
      }
    } catch (err) {
      console.log('Using fallback statuses (table may not exist yet)');
    }
  }, []);

  useEffect(() => {
    fetchStatuses();
    fetchRequests();
    fetchUsers();

    const fetchAutomations = async () => {
      try {
        const response = await automunityAPI.getAutomations();
        const result = await response.body.json() as any;
        let data: any[] = [];
        if (Array.isArray(result)) data = result;
        else if (Array.isArray(result?.records)) data = result.records;
        else if (typeof result?.records === 'string') { try { data = JSON.parse(result.records); } catch { data = []; } }
        setAutomations(data.filter((a: any) => a && a.automation_id).map((a: any) => ({ automation_id: a.automation_id, name: a.name })));
      } catch { /* no-op */ }
    };
    fetchAutomations();

    const resolveCurrentUser = async () => {
      try {
        const session = await fetchAuthSession();
        const email = (session.tokens?.idToken?.payload?.email as string || '').toLowerCase();
        if (email) {
          const response = await automunityAPI.getUsers();
          const result = await response.body.json() as any;
          let userList: User[] = [];
          if (Array.isArray(result)) userList = result;
          else if (Array.isArray(result?.records)) userList = result.records;
          else if (typeof result?.records === 'string') userList = JSON.parse(result.records);
          
          const match = userList.find((u: any) => u.email && u.email.toLowerCase() === email);
          if (match) {
            setCurrentUserId(match.user_id);
            setIsDeveloper(!!match.is_developer);
          }
        }
      } catch { /* no-op */ }
    };
    resolveCurrentUser();
  }, [fetchStatuses, fetchRequests, fetchUsers]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchRequests();
      if (closedLoaded) fetchClosedRequests(true);
    }, 120000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchRequests, fetchClosedRequests, closedLoaded]);

  // Lazily load closed/resolved history the first time a history-oriented tab opens.
  useEffect(() => {
    if ((activeTab === 'resolved' || activeTab === 'closed_without_notes') && !closedLoaded) {
      fetchClosedRequests();
    }
  }, [activeTab, closedLoaded, fetchClosedRequests]);

  const sortedUsers = [...users].sort((a, b) => {
    if (a.is_developer && !b.is_developer) return -1;
    if (!a.is_developer && b.is_developer) return 1;
    return (a.full_name || '').localeCompare(b.full_name || '');
  });

  const handleStatusChange = async (requestId: number, automationId: number, newStatus: string) => {
    const currentRequest = requests.find(r => r.request_id === requestId);
    const previousStatus = currentRequest?.status || '';
    
    try {
      await automunityAPI.updateRequest(String(automationId), requestId, { 
        status: newStatus,
        kanban_column: newStatus,
        _previous_status: previousStatus,
        _changed_by: currentUserId
      });
      setRequests(prev => prev.map(r => 
        r.request_id === requestId ? { ...r, status: newStatus } : r
      ));
      // If a request moved into or out of a closed status, refresh the open list
      // and (if already loaded) the closed history so both stay accurate.
      const closedSet = ['closed_in_production', 'closed_work_completed', 'closed_legacy', 'cancelled', 'completed'];
      const wasClosed = closedSet.includes(previousStatus);
      const nowClosed = closedSet.includes(newStatus);
      if (wasClosed !== nowClosed) {
        fetchRequests();
        if (closedLoaded) fetchClosedRequests(true);
      }
    } catch (err) {
      console.error('Error updating request status:', err);
    }
  };

  const handleDeleteRequest = async (requestId: number, automationId: number) => {
    try {
      await automunityAPI.deleteRequest(String(automationId), requestId);
      toast.success(`Request #${requestId} deleted`);
      fetchRequests();
    } catch (err) {
      console.error('Error deleting request:', err);
      toast.error('Failed to delete request');
    }
  };

  const handleAssigneeChange = async (requestId: number, automationId: number, userId: number | null) => {
    try {
      await automunityAPI.updateRequest(String(automationId), requestId, { 
        assigned_to: userId 
      });
      const assignedUser = users.find(u => u.user_id === userId);
      setRequests(prev => prev.map(r => 
        r.request_id === requestId 
          ? { ...r, assigned_to: userId || undefined, assigned_to_name: assignedUser?.full_name || undefined } 
          : r
      ));
    } catch (err) {
      console.error('Error updating assignee:', err);
    }
  };

  const handleDragStart = (e: React.DragEvent, request: Request) => {
    setDraggedRequest(request);
    e.dataTransfer.effectAllowed = 'move';
    (e.target as HTMLElement).classList.add('dragging');
  };

  const handleDragEnd = (e: React.DragEvent) => {
    (e.target as HTMLElement).classList.remove('dragging');
    setDraggedRequest(null);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, targetStatus: string) => {
    e.preventDefault();
    if (draggedRequest && draggedRequest.status !== targetStatus) {
      handleStatusChange(draggedRequest.request_id, draggedRequest.automation_id, targetStatus);
    }
    setDraggedRequest(null);
  };

  // Search filter
  const matchesSearch = (r: Request) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      r.title?.toLowerCase().includes(term) ||
      r.description?.toLowerCase().includes(term) ||
      r.automation_name?.toLowerCase().includes(term) ||
      r.assigned_to_name?.toLowerCase().includes(term) ||
      r.submitted_by_name?.toLowerCase().includes(term) ||
      String(r.request_id).includes(term) ||
      r.type?.toLowerCase().includes(term) ||
      r.priority?.toLowerCase().includes(term)
    );
  };

  // Statuses that are hidden from tabs (closed/cancelled) — accessed differently
  const HIDDEN_TAB_STATUSES = ['closed_in_production', 'closed_work_completed', 'closed_legacy', 'cancelled'];

  // Tab-based filtering: each tab = one status, plus "all" and "unassigned" and "closed_without_notes"
  const getTabFilteredRequests = () => {
    let filtered = [...requests];

    // Exclude closed/cancelled from all views (except closed_without_notes and resolved tabs)
    if (activeTab !== 'closed_without_notes' && activeTab !== 'resolved') {
      filtered = filtered.filter(r => !HIDDEN_TAB_STATUSES.includes(r.status));
    }

    // Apply search
    filtered = filtered.filter(matchesSearch);

    // Apply assignee filter (applies to all tabs EXCEPT "unassigned")
    if (activeTab !== 'unassigned') {
      if (filterAssignee === 'mine' && currentUserId) {
        filtered = filtered.filter(r => r.assigned_to === currentUserId);
      } else if (filterAssignee === '') {
        filtered = filtered.filter(r => !r.assigned_to);
      } else if (filterAssignee !== 'all' && filterAssignee !== 'mine') {
        filtered = filtered.filter(r => String(r.assigned_to || '') === filterAssignee);
      }
    }

    // Apply tab filter
    if (activeTab === 'all') {
      // Show everything not closed (already filtered above)
    } else if (activeTab === 'unassigned') {
      filtered = filtered.filter(r => !r.assigned_to);
    } else if (activeTab === 'resolved') {
      // Resolved/closed history is loaded on demand into closedRequests.
      const resolvedStatuses = ['closed_in_production', 'closed_work_completed', 'closed_legacy', 'completed', 'completed_in_review'];
      filtered = closedRequests.filter(r =>
        resolvedStatuses.includes(r.status) && matchesSearch(r)
      );
      // Apply assignee filter
      if (filterAssignee === 'mine' && currentUserId) {
        filtered = filtered.filter(r => r.assigned_to === currentUserId);
      } else if (filterAssignee === '') {
        filtered = filtered.filter(r => !r.assigned_to);
      } else if (filterAssignee !== 'all' && filterAssignee !== 'mine') {
        filtered = filtered.filter(r => String(r.assigned_to || '') === filterAssignee);
      }
    } else if (activeTab === 'closed_without_notes') {
      const closedStatuses = ['closed_in_production', 'closed_work_completed', 'closed_legacy', 'cancelled'];
      filtered = closedRequests.filter(r => 
        closedStatuses.includes(r.status) &&
        (r.time_entry_count || 0) === 0 &&
        (r.comment_count || 0) === 0 &&
        matchesSearch(r)
      );
      // Apply assignee filter to closed_without_notes too
      if (filterAssignee === 'mine' && currentUserId) {
        filtered = filtered.filter(r => r.assigned_to === currentUserId);
      } else if (filterAssignee === '') {
        filtered = filtered.filter(r => !r.assigned_to);
      } else if (filterAssignee !== 'all' && filterAssignee !== 'mine') {
        filtered = filtered.filter(r => String(r.assigned_to || '') === filterAssignee);
      }
    } else if (activeTab === 'proposed') {
      // Match requests where status OR kanban_column is 'proposed' or 'proposed_in_review'
      filtered = filtered.filter(r => 
        r.status === 'proposed' || r.status === 'proposed_in_review' || 
        r.kanban_column === 'proposed'
      );
    } else {
      filtered = filtered.filter(r => r.status === activeTab);
    }

    return filtered;
  };

  // Count requests per tab (respects assignee filter)
  const getTabCount = (tabName: string) => {
    // Start with non-closed requests for regular tabs
    let filtered = requests.filter(r => !HIDDEN_TAB_STATUSES.includes(r.status) && matchesSearch(r));

    // Apply assignee filter for counts (except unassigned tab)
    if (tabName !== 'unassigned') {
      if (filterAssignee === 'mine' && currentUserId) {
        filtered = filtered.filter(r => r.assigned_to === currentUserId);
      } else if (filterAssignee === '') {
        filtered = filtered.filter(r => !r.assigned_to);
      } else if (filterAssignee !== 'all' && filterAssignee !== 'mine') {
        filtered = filtered.filter(r => String(r.assigned_to || '') === filterAssignee);
      }
    }

    if (tabName === 'all') return filtered.length;
    if (tabName === 'unassigned') {
      return requests.filter(r => !r.assigned_to && !HIDDEN_TAB_STATUSES.includes(r.status) && matchesSearch(r)).length;
    }
    if (tabName === 'proposed') {
      // Count requests where status OR kanban_column is 'proposed' or 'proposed_in_review'
      return filtered.filter(r => 
        r.status === 'proposed' || r.status === 'proposed_in_review' || 
        r.kanban_column === 'proposed'
      ).length;
    }
    if (tabName === 'resolved') {
      const resolvedStatuses = ['closed_in_production', 'closed_work_completed', 'closed_legacy', 'completed', 'completed_in_review'];
      let resolved = closedRequests.filter(r => resolvedStatuses.includes(r.status) && matchesSearch(r));
      if (filterAssignee === 'mine' && currentUserId) {
        resolved = resolved.filter(r => r.assigned_to === currentUserId);
      } else if (filterAssignee === '') {
        resolved = resolved.filter(r => !r.assigned_to);
      } else if (filterAssignee !== 'all' && filterAssignee !== 'mine') {
        resolved = resolved.filter(r => String(r.assigned_to || '') === filterAssignee);
      }
      return resolved.length;
    }
    if (tabName === 'closed_without_notes') {
      const closedStatuses = ['closed_in_production', 'closed_work_completed', 'closed_legacy', 'cancelled'];
      let cwn = closedRequests.filter(r => 
        closedStatuses.includes(r.status) &&
        (r.time_entry_count || 0) === 0 &&
        (r.comment_count || 0) === 0 &&
        matchesSearch(r)
      );
      if (filterAssignee === 'mine' && currentUserId) {
        cwn = cwn.filter(r => r.assigned_to === currentUserId);
      } else if (filterAssignee === '') {
        cwn = cwn.filter(r => !r.assigned_to);
      } else if (filterAssignee !== 'all' && filterAssignee !== 'mine') {
        cwn = cwn.filter(r => String(r.assigned_to || '') === filterAssignee);
      }
      return cwn.length;
    }
    return filtered.filter(r => r.status === tabName).length;
  };

  const filteredRequests = getTabFilteredRequests();

  const sortRequests = (list: Request[]) => {
    return [...list].sort((a, b) => {
      const { key, direction } = sortConfig;
      let aVal: any = (a as any)[key];
      let bVal: any = (b as any)[key];
      
      if (key === 'priority') {
        const priorityOrder: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1 };
        aVal = priorityOrder[aVal] || 0;
        bVal = priorityOrder[bVal] || 0;
      } else if (key === 'deadline' || key === 'created_at') {
        aVal = aVal ? new Date(aVal).getTime() : 0;
        bVal = bVal ? new Date(bVal).getTime() : 0;
      } else if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = (bVal || '').toLowerCase();
      }

      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleSort = (key: string) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const toggleRequestSelection = (requestId: number) => {
    setSelectedRequests(prev => {
      const next = new Set(prev);
      if (next.has(requestId)) next.delete(requestId); else next.add(requestId);
      return next;
    });
  };

  const selectAllRequests = () => {
    if (selectedRequests.size === filteredRequests.length) {
      setSelectedRequests(new Set());
    } else {
      setSelectedRequests(new Set(filteredRequests.map(r => r.request_id)));
    }
  };

  const handleBulkRequestAction = async (updates: { status?: string; assigned_to?: number | null; priority?: string }) => {
    if (selectedRequests.size === 0) return;
    try {
      await automunityAPI.bulkUpdateRequests(Array.from(selectedRequests), updates);
      toast.success(`Updated ${selectedRequests.size} requests`);
      setSelectedRequests(new Set());
      fetchRequests();
      if (closedLoaded) fetchClosedRequests(true);
    } catch (err) {
      toast.error('Bulk update failed');
    }
  };

  const getStatusLabel = (statusId: string) => {
    if (statusId === 'proposed_in_review') return 'Proposed';
    return statuses.find(s => s.name === statusId)?.label || statusId;
  };

  const getStatusColor = (statusId: string) => {
    if (statusId === 'proposed_in_review') return statuses.find(s => s.name === 'proposed')?.color || '#6c757d';
    return statuses.find(s => s.name === statusId)?.color || '#6c757d';
  };

  if (loading) {
    return <div className="requests-loading">Loading requests...</div>;
  }

  // Build tab list: All + non-closed statuses + Unassigned + Closed Without Notes
  // Exclude closed/cancelled statuses from the tab bar
  const allTabs: Array<{ id: string; label: string; color?: string }> = [
    { id: 'all', label: 'All' },
    ...statuses
      .filter(s => !HIDDEN_TAB_STATUSES.includes(s.name))
      .map(s => ({ id: s.name, label: s.label, color: s.color })),
    { id: 'unassigned', label: 'Unassigned', color: '#6c757d' },
    { id: 'resolved', label: 'Resolved', color: '#28a745' },
    { id: 'closed_without_notes', label: 'Closed Without Notes', color: '#856404' },
  ];

  // Always show "All" plus the history tabs (Resolved / Closed Without Notes) —
  // their data loads on demand, so don't hide them based on a not-yet-loaded count.
  // Other status tabs only show when they have at least 1 request.
  const ALWAYS_SHOWN_TABS = ['all', 'resolved', 'closed_without_notes'];
  const tabs = allTabs.filter(tab => ALWAYS_SHOWN_TABS.includes(tab.id) || getTabCount(tab.id) > 0);

  return (
    <div className="requests-container">
      <div className="requests-header">
        <h1>Requests</h1>
        <div className="requests-controls">
          <button 
            className="create-request-btn"
            onClick={() => navigate('/chat?prompt=I want to submit a new request')}
          >
            ➕ Create Request
          </button>
          <div className="view-toggle">
            <button 
              className={view === 'table' ? 'active' : ''} 
              onClick={() => setView('table')}
            >
              📋 Table
            </button>
            <button 
              className={view === 'kanban' ? 'active' : ''} 
              onClick={() => setView('kanban')}
            >
              📌 Kanban
            </button>
          </div>
          <div className="filters">
            <input
              type="text"
              placeholder="Search requests..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ padding: '6px 12px', border: '1px solid #ddd', borderRadius: '4px', minWidth: '200px' }}
            />
            <select value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)}>
              <option value="mine">My Requests</option>
              <option value="all">All Assignees</option>
              <option value="">Unassigned</option>
              {sortedUsers.map(u => (
                <option key={u.user_id} value={String(u.user_id)}>{u.full_name}</option>
              ))}
            </select>
            <label className="auto-refresh-toggle" title="Refreshes every 2 minutes when enabled">
              <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
              Auto-refresh
            </label>
          </div>
        </div>
      </div>

      {/* Status Tabs */}
      <div className="requests-tabs">
        {tabs.map(tab => {
          const count = getTabCount(tab.id);
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              className={`requests-tab ${isActive ? 'active' : ''}`}
              onClick={() => { setActiveTab(tab.id); setSelectedRequests(new Set()); }}
              style={{
                borderBottomColor: isActive ? (tab.color || '#007bff') : 'transparent',
                color: isActive ? (tab.color || '#007bff') : undefined,
              }}
            >
              {tab.label}
              <span className="tab-count" style={{ background: isActive ? (tab.color || '#007bff') : undefined }}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {view === 'table' ? (
        <>
          {selectedRequests.size > 0 && (
            <div className="bulk-action-bar">
              <span className="bulk-count">{selectedRequests.size} selected</span>
              <select onChange={e => { if (e.target.value) { handleBulkRequestAction({ status: e.target.value }); e.target.value = ''; } }}>
                <option value="">Change Status...</option>
                {statuses.map(s => <option key={s.name} value={s.name}>{s.label}</option>)}
              </select>
              <select onChange={e => { if (e.target.value !== '') { handleBulkRequestAction({ assigned_to: e.target.value ? Number(e.target.value) : null }); e.target.value = ''; } }}>
                <option value="">Assign To...</option>
                <option value="0">Unassign</option>
                {sortedUsers.map(u => <option key={u.user_id} value={String(u.user_id)}>{u.full_name}</option>)}
              </select>
              <button className="btn btn-sm btn-secondary" onClick={() => setSelectedRequests(new Set())}>Cancel</button>
            </div>
          )}
          <TableView 
            requests={sortRequests(filteredRequests)} 
            users={sortedUsers}
            statuses={statuses}
            onStatusChange={handleStatusChange}
            onAssigneeChange={handleAssigneeChange}
            onClickRequest={(id) => window.open(`/requests/${id}`, '_blank')}
            onClickAutomation={(id) => navigate(`/automations/view/${id}`)}
            getStatusLabel={getStatusLabel}
            getStatusColor={getStatusColor}
            selectedRequests={selectedRequests}
            onToggleSelect={toggleRequestSelection}
            onSelectAll={selectAllRequests}
            sortConfig={sortConfig}
            onSort={handleSort}
            onDeleteRequest={handleDeleteRequest}
            isDeveloper={isDeveloper}
            showQuickActions={activeTab === 'closed_without_notes'}
            onQuickResolve={async (requestId: number) => {
              try {
                await automunityAPI.createTimeEntry(requestId, {
                  hours: 0.25,
                  notes: 'Fix implemented with Kiro, tested by developer',
                  user_id: currentUserId
                });
                setRequests(prev => prev.map(r => 
                  r.request_id === requestId ? { ...r, time_entry_count: (r.time_entry_count || 0) + 1 } : r
                ));
                toast.success(`Time entry added to #${requestId}`);
              } catch (err) {
                toast.error('Failed to add time entry');
              }
            }}
            onQuickNote={(requestId: number, title: string) => {
              setQuickNoteRequestId(requestId);
              setQuickNoteTitle(title);
            }}
          />
          {activeTab === 'closed_without_notes' && filteredRequests.length > 1 && (
            <div style={{ marginTop: '10px' }}>
              <button
                onClick={async () => {
                  for (const req of filteredRequests) {
                    try {
                      await automunityAPI.createTimeEntry(req.request_id, {
                        hours: 0.25,
                        notes: 'Fix implemented with Kiro, tested by developer',
                        user_id: currentUserId
                      });
                    } catch { /* continue */ }
                  }
                  toast.success(`Quick resolved ${filteredRequests.length} requests`);
                  fetchRequests();
                }}
                style={{ padding: '6px 14px', fontSize: '13px', background: '#ffc107', color: '#212529', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 500 }}
              >
                🤖 Quick Resolve All ({filteredRequests.length})
              </button>
            </div>
          )}
        </>

      ) : (
        <KanbanView 
          requests={filteredRequests}
          users={sortedUsers}
          statuses={statuses}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onAssigneeChange={handleAssigneeChange}
          onClickRequest={(id) => window.open(`/requests/${id}`, '_blank')}
          getStatusColor={getStatusColor}
          draggedRequest={draggedRequest}
        />
      )}

      {/* Quick Note Modal */}
      {quickNoteRequestId && (
        <div className="modal-overlay" onClick={() => { setQuickNoteRequestId(null); setQuickNoteText(''); setQuickNoteHours(''); }}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>📝 Quick Note — #{quickNoteRequestId}</h3>
            <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '12px' }}>{quickNoteTitle}</p>
            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                if (quickNoteText.trim()) {
                  await automunityAPI.createRequestComment(quickNoteRequestId, { comment: quickNoteText, user_id: currentUserId });
                }
                if (quickNoteHours) {
                  await automunityAPI.createTimeEntry(quickNoteRequestId, { hours: parseFloat(quickNoteHours), notes: quickNoteText || 'Work completed', user_id: currentUserId });
                }
                toast.success('Note added');
                setQuickNoteRequestId(null);
                setQuickNoteText('');
                setQuickNoteHours('');
                fetchRequests();
              } catch { toast.error('Failed to add note'); }
            }}>
              <div className="form-group">
                <label>Comment / Notes</label>
                <textarea className="form-control" value={quickNoteText} onChange={e => setQuickNoteText(e.target.value)} placeholder="What was done? How was it resolved?" rows={3} />
              </div>
              <div className="form-group">
                <label>Time Spent (hours, optional)</label>
                <input type="number" step="0.25" min="0.25" className="form-control" value={quickNoteHours} onChange={e => setQuickNoteHours(e.target.value)} placeholder="e.g., 0.25" />
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button type="submit" className="btn btn-success" disabled={!quickNoteText.trim() && !quickNoteHours}>Save</button>
                <button type="button" className="btn btn-secondary" onClick={() => { setQuickNoteRequestId(null); setQuickNoteText(''); setQuickNoteHours(''); }}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Helper: deadline color styling
const getDeadlineStyle = (deadline: string): React.CSSProperties => {
  const d = new Date(deadline);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  if (d < today) return { color: '#dc3545', fontWeight: 600 };
  let bizDays = 0;
  const check = new Date(today);
  while (bizDays < 5) {
    check.setDate(check.getDate() + 1);
    if (check.getDay() !== 0 && check.getDay() !== 6) bizDays++;
  }
  if (d <= check) return { color: '#e67e22', fontWeight: 600 };
  return {};
};

// Table View Component
interface TableViewProps {
  requests: Request[];
  users: User[];
  statuses: StatusConfig[];
  onStatusChange: (id: number, automationId: number, status: string) => void;
  onAssigneeChange: (id: number, automationId: number, userId: number | null) => void;
  onClickRequest: (id: number) => void;
  onClickAutomation: (id: number) => void;
  onDeleteRequest?: (id: number, automationId: number) => void;
  getStatusLabel: (id: string) => string;
  getStatusColor: (id: string) => string;
  selectedRequests: Set<number>;
  onToggleSelect: (id: number) => void;
  onSelectAll: () => void;
  sortConfig: { key: string; direction: 'asc' | 'desc' };
  onSort: (key: string) => void;
  isDeveloper?: boolean;
  showQuickActions?: boolean;
  onQuickResolve?: (id: number) => void;
  onQuickNote?: (id: number, title: string) => void;
}

const TableView: React.FC<TableViewProps> = ({ requests, users, statuses, onStatusChange, onAssigneeChange, onClickRequest, onClickAutomation, onDeleteRequest, getStatusLabel, getStatusColor, selectedRequests, onToggleSelect, onSelectAll, sortConfig, onSort, isDeveloper, showQuickActions, onQuickResolve, onQuickNote }) => {
  const SortHeader: React.FC<{ label: string; sortKey: string }> = ({ label, sortKey }) => (
    <th onClick={() => onSort(sortKey)} style={{ cursor: 'pointer', userSelect: 'none' }}>
      {label} {sortConfig.key === sortKey ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
    </th>
  );

  return (
    <div className="requests-table-container">
      <table className="requests-table">
        <thead>
          <tr>
            <th><input type="checkbox" checked={selectedRequests.size === requests.length && requests.length > 0} onChange={onSelectAll} /></th>
            <SortHeader label="ID" sortKey="request_id" />
            <SortHeader label="Type" sortKey="type" />
            <SortHeader label="Title" sortKey="title" />
            <SortHeader label="Automation" sortKey="automation_name" />
            <SortHeader label="Priority" sortKey="priority" />
            <th>Status</th>
            <th>Assigned To</th>
            <SortHeader label="Deadline" sortKey="deadline" />
            <SortHeader label="Created" sortKey="created_at" />
            {(isDeveloper || showQuickActions) && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {requests.length === 0 ? (
            <tr><td colSpan={11} className="no-data">No requests found</td></tr>
          ) : (
            requests.map(req => (
              <tr key={req.request_id} className={selectedRequests.has(req.request_id) ? 'selected-row' : ''}>
                <td><input type="checkbox" checked={selectedRequests.has(req.request_id)} onChange={() => onToggleSelect(req.request_id)} /></td>
                <td className="req-id" onClick={() => onClickRequest(req.request_id)} style={{ cursor: 'pointer' }}>#{req.request_id}</td>
                <td className="req-type">{TYPE_ICONS[req.type] || '📝'} {req.type}</td>
                <td className="req-title" onClick={() => onClickRequest(req.request_id)} style={{ cursor: 'pointer' }}>{req.title}</td>
                <td className="req-automation">
                  {req.automation_name ? (
                    <span onClick={() => onClickAutomation(req.automation_id)} style={{ color: '#007bff', cursor: 'pointer' }}>
                      {req.automation_name}
                    </span>
                  ) : '-'}
                </td>
                <td>
                  <span className="priority-badge" style={{ background: PRIORITY_COLORS[req.priority] || '#6c757d' }}>
                    {req.priority}
                  </span>
                </td>
                <td>
                  <select 
                    value={req.status} 
                    onChange={e => onStatusChange(req.request_id, req.automation_id, e.target.value)}
                    style={{ borderLeft: `3px solid ${getStatusColor(req.status)}` }}
                  >
                    {statuses.map(s => (
                      <option key={s.name} value={s.name}>{s.label}</option>
                    ))}
                  </select>
                </td>

                <td>
                  <select 
                    value={String(req.assigned_to || '')}
                    onChange={e => onAssigneeChange(req.request_id, req.automation_id, e.target.value ? Number(e.target.value) : null)}
                  >
                    <option value="">Unassigned</option>
                    {users.map(u => (
                      <option key={u.user_id} value={String(u.user_id)}>{u.full_name}</option>
                    ))}
                  </select>
                </td>
                <td className="req-date">
                  {req.deadline ? (
                    <span style={getDeadlineStyle(req.deadline)}>
                      {new Date(req.deadline).toLocaleDateString()}
                    </span>
                  ) : '—'}
                </td>
                <td className="req-date">{new Date(req.created_at).toLocaleDateString()}</td>
                {(isDeveloper || showQuickActions) && (
                  <td style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                    {showQuickActions && onQuickResolve && (
                      <button
                        onClick={() => onQuickResolve(req.request_id)}
                        style={{ padding: '4px 8px', fontSize: '11px', background: '#28a745', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >🤖 Quick Resolve</button>
                    )}
                    {showQuickActions && onQuickNote && (
                      <button
                        onClick={() => onQuickNote(req.request_id, req.title)}
                        style={{ padding: '4px 8px', fontSize: '11px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >📝 Quick Note</button>
                    )}
                    {isDeveloper && onDeleteRequest && (
                      <button
                        onClick={() => { if (window.confirm(`Delete request #${req.request_id}?`)) onDeleteRequest(req.request_id, req.automation_id); }}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc3545', fontSize: '16px' }}
                        title="Delete request"
                      >🗑️</button>
                    )}
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

// Kanban View Component
interface KanbanViewProps {
  requests: Request[];
  users: User[];
  statuses: StatusConfig[];
  onDragStart: (e: React.DragEvent, request: Request) => void;
  onDragEnd: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent, status: string) => void;
  onAssigneeChange: (id: number, automationId: number, userId: number | null) => void;
  onClickRequest: (id: number) => void;
  getStatusColor: (id: string) => string;
  draggedRequest: Request | null;
}

const HIDDEN_KANBAN_STATUSES = ['backlogged', 'cancelled'];

const KanbanView: React.FC<KanbanViewProps> = ({ 
  requests, users, statuses, onDragStart, onDragEnd, onDragOver, onDrop, onAssigneeChange, onClickRequest, getStatusColor, draggedRequest 
}) => {
  const visibleStatuses = statuses.filter(s => !HIDDEN_KANBAN_STATUSES.includes(s.name));

  return (
    <div className="kanban-board">
      {visibleStatuses.map(status => {
        const columnRequests = requests.filter(r => r.status === status.name);
        const isDropTarget = draggedRequest && draggedRequest.status !== status.name;
        
        return (
          <div 
            key={status.name} 
            className={`kanban-column ${isDropTarget ? 'drop-target' : ''}`}
            onDragOver={onDragOver}
            onDrop={e => onDrop(e, status.name)}
          >
            <div className="kanban-column-header" style={{ borderTopColor: status.color }}>
              <span className="column-title">{status.label}</span>
              <span className="column-count">{columnRequests.length}</span>
            </div>
            <div className="kanban-column-body">
              {columnRequests.map(req => (
                <div 
                  key={req.request_id}
                  className="kanban-card"
                  draggable
                  onDragStart={e => onDragStart(e, req)}
                  onDragEnd={onDragEnd}
                >
                  <div className="card-header">
                    <span className="card-type">{TYPE_ICONS[req.type] || '📝'}</span>
                    <span className="card-id">#{req.request_id}</span>
                    <span className="card-priority" style={{ background: PRIORITY_COLORS[req.priority] }}>
                      {req.priority}
                    </span>
                  </div>
                  <div className="card-title" onClick={() => onClickRequest(req.request_id)} style={{ cursor: 'pointer' }}>{req.title}</div>
                  {req.automation_name && (
                    <div className="card-automation">{req.automation_name}</div>
                  )}

                  <div className="card-footer">
                    <select 
                      className="card-assignee"
                      value={String(req.assigned_to || '')}
                      onChange={e => {
                        e.stopPropagation();
                        onAssigneeChange(req.request_id, req.automation_id, e.target.value ? Number(e.target.value) : null);
                      }}
                      onClick={e => e.stopPropagation()}
                    >
                      <option value="">👤 Unassigned</option>
                      {users.map(u => (
                        <option key={u.user_id} value={String(u.user_id)}>{u.full_name}</option>
                      ))}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Requests;
